import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  DROPDOWN_SIZE,
  DSButton,
  DSModal,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
} from '@repo/design-system/index'
import { QIcon_location } from '@repo/icons/index'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns-tz'
import { Dispatch, useCallback, useEffect, useState } from 'react'
import { ITutor } from '../../../../model/tutor.interface'
import { useRemainingCreditsCount } from '../../../../modules/apis/student/credit'
import {
  useApplyLessons,
  useAvailableCoupon,
  useAvailableCouponBundles,
  useAvailableTimesForTutor,
  useHoldTimeslot,
  useStartTimeLimit,
} from '../../../../modules/apis/student/lesson/schedule/queries'
import {
  AvailableCoupon,
  AvailableCouponBundlesResponse,
  IApplyInfo,
  ISchedule,
  ITimeslot,
} from '../../../../modules/apis/student/lesson/schedule/types'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_book_lessons, n_common, n_tutors, s_schedule } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { upcomingsQueryApiV5 } from '../../../../modules/react-query/queryApis'
import urls from '../../../../modules/urls'
import { COLORS, TUTOR_SCHEDULE_OPEN_STATUS } from '../../../../modules/vars'
import { useSidePopup } from '../../../basic/popup/SidePopup'
import LoadingSpinner from '../../../common/LoadingSpinner'
import NoPackageModal from '../../../new-portal/lessons/schedules/NoPackageModal'
import PackageSelectModal from '../../../new-portal/lessons/schedules/PackageSelectModal'
import { ScheduleCalendar } from '../../../new-portal/lessons/schedules/ScheduleCalendar'
import ScheduleModalBody from '../../../new-portal/lessons/schedules/ScheduleModalBody'
import TimeSlotUnavailableModal from '../../../new-portal/lessons/schedules/TimeSlotUnavailableModal'
import TodayScheduleAlertModal from '../../../new-portal/lessons/schedules/TodayScheduleAlertModal'

const TimeChipItem = ({
  timeslot,
  handleSelectTime,
}: {
  timeslot: ITimeslot
  handleSelectTime: (time: any) => void
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const getTimeStr = (timeslot: ITimeslot) => {
    let timeStr = ''
    const { hour, minute } = timeslot
    const isAM = hour < 12 || hour >= 24
    const hours12 = hour % 12 === 0 ? 12 : hour % 12
    const hourStr = isAM ? t(s_schedule.AM_hour(hours12)) : t(s_schedule.PM_hour(hours12))

    timeStr = hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0')

    return {
      isAM: isAM,
      isPM: !isAM,
      hour: hour,
      hour12: hours12,
      minute: minute,
      hourStr: hourStr,
      timeStr: timeStr,
    }
  }

  const getCustomBgColor = (timeslot: ITimeslot) => {
    if (!timeslot.is_available && !timeslot.is_opened) {
      return COLORS.GRAY200
    } else if (timeslot.is_today) {
      return COLORS.RED50
    } else {
      return COLORS.WHITE
    }
  }

  const getOpacity = (timeslot: ITimeslot) => {
    if (!timeslot.is_available && timeslot.is_opened) {
      return 'opacity-30'
    } else {
      return ''
    }
  }

  return (
    <div>
      <DSButton
        customBgColor={getCustomBgColor(timeslot)}
        disabled={!timeslot.is_available && !timeslot.is_opened}
        type={BUTTON_TYPE.OUTLINE}
        size={BUTTON_SIZE.SM}
        className={getOpacity(timeslot)}
        onClick={(e) => handleSelectTime(timeslot)}
      >
        <DSButton.Label>{getTimeStr(timeslot).timeStr}</DSButton.Label>
      </DSButton>
    </div>
  )
}
export const initialApplyInfoData = (duration = 40) => {
  return {
    id: 1,
    duration,
    time: {
      prev: null,
      current: {
        classtimeId: null,
        classtime: null,
        display: null,
        displayFull: null,
      },
      next: null,
    },
    selectedTime: null,
    selectedTimeslot: null,
    selectedCoupon: null,
    date: new Date(),
    tutor: null,
  }
}
const enum STEP {
  SCHEDULE = 1,
  COMPLETE = 2,
}
const TutorScheduleLesson = ({ tutor, setResult, size }: { tutor: ITutor; setResult: Dispatch<any>; size: string }) => {
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const isMobile = useIsMobile()
  const { t, isLocaleKO } = useTranslation()
  const sidePopup = useSidePopup()
  const [schedules, setSchedules] = useState<ISchedule[]>([])
  const [applyInfo, setApplyInfo] = useState<IApplyInfo>(
    initialApplyInfoData(router?.query.duration ? +router?.query.duration : 40)
  )
  const [couponBundlesData, setCouponBundlesData] = useState(null)
  const [showPackageSelectModal, setShowPackageSelectModal] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState('')
  const [showErrorMessagePopup, setShowErrorMessagePopup] = useState(false)
  const [showNoAvailablePackage, setShowNoAvailablePackage] = useState(false)
  const [showTodayScheduleModal, setShowTodayScheduleModal] = useState(false)
  const [showNoAvailableCreditsMessage, setShowNoAvailableCreditsMessage] = useState('')
  const [showNoAvailableCreditsPopup, setShowNoAvailableCreditsPopup] = useState(false)
  const [step, setStep] = useState(STEP.SCHEDULE)
  const [selectedDay, setSelectedDay] = useState<Date>()
  const { currentUser } = useCurrentUser()
  const [availableTimes, setAvailableTimes] = useState([])
  const queryClient = useQueryClient()

  const durationOptionList = [
    {
      label: t(n_common.label_20min),
      value: 20,
      disabled: false,
    },
    {
      label: t(n_common.label_40min),
      value: 40,
      disabled: false,
    },
  ]
  const [duration, setDuration] = useState(
    router?.query.duration
      ? durationOptionList.find((option) => option.value == +router?.query.duration)
      : durationOptionList[0]
  )
  const [initialLoad, setInitialLoad] = useState(true)

  const lastLessonDuration = currentUser?.braze?.lesson?.last_reserved_lesson_duration

  useRemainingCreditsCount({
    onSuccess: (data) => {
      const creditCount40 =
        data.regular_40_credit_count +
        data.makeup_40_credit_count +
        data.timelimit_40_credit_count +
        data.urgent_40_credit_count
      const creditCount20 =
        data.regular_20_credit_count +
        data.makeup_20_credit_count +
        data.timelimit_20_credit_count +
        data.urgent_20_credit_count

      let selectedDuration = null
      if (creditCount20 > 0 && creditCount40 == 0) {
        selectedDuration = 20
      } else if (creditCount40 > 0 && creditCount20 == 0) {
        selectedDuration = 40
      } else {
        selectedDuration = lastLessonDuration ? lastLessonDuration : 40
      }

      setDuration(durationOptionList.find((durationOption) => durationOption.value == selectedDuration))
    },
  })

  const { data: timeMessage } = useStartTimeLimit(duration.value, [])

  const { isFetching } = useAvailableTimesForTutor(tutor?.id, duration.value, {
    onSuccess: (data) => {
      if (!initialLoad && availableTimes == data && availableTimes.length > 0) return
      setAvailableTimes(data)
      setInitialLoad(false)
      if (data.length > 0) {
        const date = new Date()
        let found = false
        let firstAvailableTime = null
        let times = []
        const enabledDates = data?.filter(
          (classtime) => classtime?.timeslots?.filter((time) => time.is_opened)?.length > 0
        )
        if (enabledDates.length > 0) {
          while (!found) {
            const selectedDate = format(date, 'yyyy-MM-dd')
            times = data.find((item) => item.date == selectedDate)?.timeslots
            const availableTimes = times?.filter((item) => item.is_opened)

            if (availableTimes && availableTimes.length >= 1) {
              firstAvailableTime = availableTimes[0]
              found = true
            } else {
              date.setDate(date.getDate() + 1)
            }
          }
          if (firstAvailableTime) {
            setSelectedDay(
              new Date(
                firstAvailableTime.year,
                firstAvailableTime.month - 1,
                firstAvailableTime.day,
                firstAvailableTime.hour
              )
            )
          }
          setApplyInfo({
            ...applyInfo,
            duration: duration.value,
          })
        }
      } else {
        setSelectedDay(new Date())
      }
    },
  })
  const { mutate: holdTimeslot } = useHoldTimeslot(applyInfo.selectedTimeslot?.id)
  useAvailableCouponBundles(applyInfo.selectedTimeslot?.classtime_id, applyInfo?.duration, {
    onSuccess: (data: AvailableCouponBundlesResponse) => {
      setCouponBundlesData(data)
      let list = []
      const lastMinutesCoupons = data.last_minute_coupons
      const urgentCoupons = data.urgent_coupons
      list = list.concat(data.packages)
      if (lastMinutesCoupons?.is_available) {
        list.push(lastMinutesCoupons)
      }
      if (urgentCoupons?.is_available) {
        list.push(urgentCoupons)
      }
      const availablePackages = list.filter((data) => data.is_available)
      if (applyInfo.selectedTimeslot.id) {
        if (availablePackages.length > 0) {
          holdTimeslot(
            { duration: applyInfo.duration },
            {
              onSuccess: (data) => {
                if (data.error) {
                  setShowErrorMessage(data.error)
                  setShowErrorMessagePopup(true)
                } else {
                  if (availablePackages.length > 1) {
                    setShowPackageSelectModal(true)
                  } else {
                    const availablePackage = availablePackages[0]
                    if (
                      availablePackage.coupon_bundle_id &&
                      availablePackage.coupon_bundle_id == applyInfo.selectedCoupon?.couponBundleId
                    ) {
                      refetchAvailableCoupon()
                    } else if (
                      availablePackage.coupon_type_id &&
                      availablePackage.coupon_type_id == applyInfo.selectedCoupon?.couponTypeId
                    ) {
                      refetchAvailableCoupon()
                    } else {
                      if (availablePackage.coupon_bundle_id) {
                        setApplyInfo({
                          ...applyInfo,
                          selectedCoupon: {
                            ...applyInfo.selectedCoupon,
                            couponBundleId: availablePackage.coupon_bundle_id,
                            isWeeklyLessonPackage: availablePackage.is_weekly_lesson_package,
                          },
                        })
                      } else {
                        setApplyInfo({
                          ...applyInfo,
                          selectedCoupon: {
                            ...applyInfo.selectedCoupon,
                            couponTypeId: availablePackage.coupon_type_id,
                            isWeeklyLessonPackage: availablePackage.is_weekly_lesson_package,
                          },
                        })
                      }
                    }
                  }
                }
              },
            }
          )
        } else {
          setApplyInfo({
            ...applyInfo,
            selectedTimeslot: null,
          })
          setShowNoAvailablePackage(true)
        }
      }
    },
    onError: (error) => {
      if (error?.response?.data?.error) {
        setShowNoAvailableCreditsMessage(error?.response?.data?.error)
        setShowNoAvailableCreditsPopup(true)
      }
    },
  })

  const { refetch: refetchAvailableCoupon } = useAvailableCoupon(
    applyInfo,
    schedules.map((schedule) => schedule.couponId),
    {
      onSuccess: (data: AvailableCoupon) => {
        const newData = {
          ...applyInfo,
          tutor: tutor,
          couponId: data.id,
          matchingAfter: 1,
        }
        const tempSchedules = schedules
        const isDuplicate = tempSchedules.some((item) => item.selectedTimeslot?.id === newData.selectedTimeslot?.id)
        if (!isDuplicate) {
          tempSchedules.push(newData)
          showSidePopup()
        }
        setSchedules(tempSchedules)
      },
    }
  )

  const handleSelectTime = (timeslot) => {
    const applyData = {
      ...applyInfo,
      selectedTimeslot: timeslot,
    }
    setApplyInfo(applyData)
  }

  const resetTimeslot = () => {
    const applyData = {
      ...applyInfo,
      selectedTimeslot: null,
    }
    setApplyInfo(applyData)
  }
  const closeSidePopup = () => {
    sidePopup.close('lesson-popup')
  }

  const { mutateAsync: apply } = useApplyLessons()

  useEffect(() => {
    if (schedules.length > 0) {
      showSidePopup()
    }
  }, [schedules])

  const showSidePopup = () => {
    sidePopup.show({
      id: 'lesson-popup',
      overlayClose: true,
      border: true,
      bgWhite: false,
      maxW: 405,
      hFull: true,
      gapFromTop: 0,
      noTopSide: true,
      onClose: () => {
        handleDeleteConfirm()
      },
      body: (
        <ScheduleModalBody
          setSchedules={setSchedules}
          schedules={schedules}
          handleDelete={handleDeleteConfirm}
          handleSubmit={handleSubmit}
        />
      ),
    })
  }

  const [isTimeSlotUnavailableModalOpen, setIsTimeSlotUnavailableModalOpen] = useState(false)
  const handleSubmit = useCallback(
    async (skip = false) => {
      const payload = schedules.map((schedule, i) => {
        return schedule.selectedCoupon.couponBundleId
          ? {
              duration: schedule.duration,
              matching_after: schedule.matchingAfter,
              timeslot_id: schedule.selectedTimeslot.id,
              coupon_bundle_id: schedule.selectedCoupon.couponBundleId,
            }
          : {
              duration: schedule.duration,
              matching_after: schedule.matchingAfter,
              timeslot_id: schedule.selectedTimeslot.id,
              coupon_type_id: schedule.selectedCoupon.couponTypeId,
            }
      })
      const response = await apply({ application_objects: payload })
      if (response) {
        const isSuccess = response.results.every((result) => result.success)

        if (isSuccess) {
          sidePopup.close('lesson-popup')
          setShowTodayScheduleModal(false)
          setResult({
            data: response,
            schedules: schedules,
          })
          setStep(STEP.COMPLETE)
        } else {
          setIsTimeSlotUnavailableModalOpen(true)
        }

        queryClient.invalidateQueries(upcomingsQueryApiV5.getQueryKey())
      }
    },
    [schedules]
  )
  const handleDeleteConfirm = () => {
    closeSidePopup()
    setSchedules([])
    setApplyInfo({
      ...applyInfo,
      selectedTimeslot: null,
      selectedCoupon: null,
    })
  }

  const handleDuration = (option) => {
    setDuration(option)
    setApplyInfo({
      ...applyInfo,
      duration: option.value,
      selectedTimeslot: null,
    })
  }

  const dateTitle = (date: Date, isLocaleKO = true) => {
    if (!date) return ''
    const monthFormatted = date.getMonth() + 1
    const dayFormatted = date.getDate()
    const dayOfWeek = (
      isLocaleKO ? ['일', '월', '화', '수', '목', '금', '토'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    )[date.getDay()]

    return `${monthFormatted}.${dayFormatted} (${dayOfWeek})`
  }

  const isEmpty = () => {
    return !availableTimes?.find((time) => time.timeslots.filter((timeslot) => timeslot.is_opened).length > 0)
  }

  const currentTimeslots = selectedDay
    ? availableTimes?.find((time) => time.date == format(selectedDay, 'yyyy-MM-dd'))?.timeslots
    : []

  if (tutor?.schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.FULL_BOOKED) {
    return (
      <div className="mt-[20px] flex h-full w-full flex-row flex-col items-center justify-center gap-x-[20px] divide-x divide-[1px] divide-gray-200 pt-[64px] text-center">
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path
              d="M53.7546 51.0879C53.7546 53.8009 51.5553 56.0002 48.8424 56.0002H15.1583C12.4454 56.0002 10.2461 53.8009 10.2461 51.0879V26.5265H53.7546V51.0879Z"
              fill="#E4E7F4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M22.175 8C23.4927 8 24.561 9.06823 24.561 10.386V14.0351C24.561 15.3528 23.4927 16.421 22.175 16.421C20.8573 16.421 19.7891 15.3528 19.7891 14.0351V10.386C19.7891 9.06823 20.8573 8 22.175 8Z"
              fill="#E4E7F4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M41.8235 8C43.1412 8 44.2094 9.06823 44.2094 10.386V14.0351C44.2094 15.3528 43.1412 16.421 41.8235 16.421C40.5057 16.421 39.4375 15.3528 39.4375 14.0351V10.386C39.4375 9.06823 40.5057 8 41.8235 8Z"
              fill="#E4E7F4"
            />
            <path
              d="M10.2461 17.4037C10.2461 14.6907 12.4454 12.4915 15.1583 12.4915H48.8424C51.5553 12.4915 53.7546 14.6907 53.7546 17.4037V22.3374H10.2461V17.4037Z"
              fill="#E4E7F4"
            />
          </svg>
        </div>
        <div className="Title2-18Md mt-[20px]">{t(n_tutors.fully_booked_title)}</div>
      </div>
    )
  }
  if (tutor?.schedule_status == TUTOR_SCHEDULE_OPEN_STATUS.NOT_OPEN_YET) {
    return (
      <div className="mt-[20px] flex h-full w-full flex-row flex-col items-center justify-center gap-x-[20px] divide-x divide-[1px] divide-gray-200 pt-[64px] text-center">
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
            <path
              d="M53.7546 51.0879C53.7546 53.8009 51.5553 56.0002 48.8424 56.0002H15.1583C12.4454 56.0002 10.2461 53.8009 10.2461 51.0879V26.5265H53.7546V51.0879Z"
              fill="#E4E7F4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M22.175 8C23.4927 8 24.561 9.06823 24.561 10.386V14.0351C24.561 15.3528 23.4927 16.421 22.175 16.421C20.8573 16.421 19.7891 15.3528 19.7891 14.0351V10.386C19.7891 9.06823 20.8573 8 22.175 8Z"
              fill="#E4E7F4"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M41.8235 8C43.1412 8 44.2094 9.06823 44.2094 10.386V14.0351C44.2094 15.3528 43.1412 16.421 41.8235 16.421C40.5057 16.421 39.4375 15.3528 39.4375 14.0351V10.386C39.4375 9.06823 40.5057 8 41.8235 8Z"
              fill="#E4E7F4"
            />
            <path
              d="M10.2461 17.4037C10.2461 14.6907 12.4454 12.4915 15.1583 12.4915H48.8424C51.5553 12.4915 53.7546 14.6907 53.7546 17.4037V22.3374H10.2461V17.4037Z"
              fill="#E4E7F4"
            />
          </svg>
        </div>
        <div className="Title2-18Md mt-[20px]">{t(n_tutors.no_schedule_content)}</div>
      </div>
    )
  }

  return (
    <div className="newTheme px-[16px]">
      {step === STEP.SCHEDULE &&
        (isFetching ? (
          <LoadingSpinner show md primary />
        ) : (
          <div>
            {size == 'sm' ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="w-full">
                    <Dropdown selected={duration} setSelected={handleDuration} size={DROPDOWN_SIZE.MD}>
                      <DropdownButton>{duration?.label}</DropdownButton>
                      <DropdownMenu>
                        {durationOptionList.map((item, i) => {
                          return (
                            <DropdownMenuItem key={i} value={item.value} disabled={item.disabled}>
                              {item.label}
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
                <div className="Body2-14Md mt-[16px] text-gray-500">{timeMessage?.message}</div>
                <div className="my-[16px] h-[1px] w-full bg-gray-200" />
                {!isEmpty() && (
                  <div className="Body2-14Md flex items-center text-gray-600">
                    <QIcon_location className="mr-[4px]" color={COLORS.GRAY600} />
                    {currentUser?.timezone}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="w-full sm:max-w-[150px]">
                    <Dropdown selected={duration} setSelected={handleDuration} size={DROPDOWN_SIZE.MD}>
                      <DropdownButton>{duration?.label}</DropdownButton>
                      <DropdownMenu>
                        {durationOptionList.map((item, i) => {
                          return (
                            <DropdownMenuItem key={i} value={item.value} disabled={item.disabled}>
                              {item.label}
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                  <div className="Body2-14Md mt-[32px] flex items-center text-gray-600 sm:mt-[0px]">
                    <QIcon_location className="mr-[4px]" color={COLORS.GRAY600} />
                    {currentUser?.timezone}
                  </div>
                </div>
                <div className="Body2-14Md mt-[16px] text-gray-500">{timeMessage?.message}</div>
              </>
            )}
            {isEmpty() ? (
              <div className="mt-[20px] flex h-full w-full flex-row flex-col items-center justify-center gap-x-[20px] pt-[64px] text-center">
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <path
                      d="M53.7546 51.0879C53.7546 53.8009 51.5553 56.0002 48.8424 56.0002H15.1583C12.4454 56.0002 10.2461 53.8009 10.2461 51.0879V26.5265H53.7546V51.0879Z"
                      fill="#E4E7F4"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M22.175 8C23.4927 8 24.561 9.06823 24.561 10.386V14.0351C24.561 15.3528 23.4927 16.421 22.175 16.421C20.8573 16.421 19.7891 15.3528 19.7891 14.0351V10.386C19.7891 9.06823 20.8573 8 22.175 8Z"
                      fill="#E4E7F4"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M41.8235 8C43.1412 8 44.2094 9.06823 44.2094 10.386V14.0351C44.2094 15.3528 43.1412 16.421 41.8235 16.421C40.5057 16.421 39.4375 15.3528 39.4375 14.0351V10.386C39.4375 9.06823 40.5057 8 41.8235 8Z"
                      fill="#E4E7F4"
                    />
                    <path
                      d="M10.2461 17.4037C10.2461 14.6907 12.4454 12.4915 15.1583 12.4915H48.8424C51.5553 12.4915 53.7546 14.6907 53.7546 17.4037V22.3374H10.2461V17.4037Z"
                      fill="#E4E7F4"
                    />
                  </svg>
                </div>
                <div className="Title2-18Md mt-[20px]">
                  {t(duration.value == 20 ? n_tutors.fully_booked_20_min : n_tutors.fully_booked_40_min)}
                </div>
              </div>
            ) : (
              <>
                {!currentTimeslots || (currentTimeslots && currentTimeslots.length == 0) ? (
                  <div className="mt-[20px] flex h-full w-full flex-row flex-col items-center justify-center gap-x-[20px] pt-[64px] text-center">
                    <div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none">
                        <path
                          d="M53.7546 51.0879C53.7546 53.8009 51.5553 56.0002 48.8424 56.0002H15.1583C12.4454 56.0002 10.2461 53.8009 10.2461 51.0879V26.5265H53.7546V51.0879Z"
                          fill="#E4E7F4"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M22.175 8C23.4927 8 24.561 9.06823 24.561 10.386V14.0351C24.561 15.3528 23.4927 16.421 22.175 16.421C20.8573 16.421 19.7891 15.3528 19.7891 14.0351V10.386C19.7891 9.06823 20.8573 8 22.175 8Z"
                          fill="#E4E7F4"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M41.8235 8C43.1412 8 44.2094 9.06823 44.2094 10.386V14.0351C44.2094 15.3528 43.1412 16.421 41.8235 16.421C40.5057 16.421 39.4375 15.3528 39.4375 14.0351V10.386C39.4375 9.06823 40.5057 8 41.8235 8Z"
                          fill="#E4E7F4"
                        />
                        <path
                          d="M10.2461 17.4037C10.2461 14.6907 12.4454 12.4915 15.1583 12.4915H48.8424C51.5553 12.4915 53.7546 14.6907 53.7546 17.4037V22.3374H10.2461V17.4037Z"
                          fill="#E4E7F4"
                        />
                      </svg>
                    </div>
                    <div className="Title2-18Md mt-[20px]">{t(n_tutors.fully_booked_title)}</div>
                  </div>
                ) : (
                  <div className="mt-[8px]">
                    {size == 'sm' ? (
                      <>
                        {/* 날짜 */}
                        <div className="mt-[14px]">
                          {selectedDay && (
                            <ScheduleCalendar
                              selectedDay={selectedDay}
                              setSelectedDay={setSelectedDay}
                              classtimes={availableTimes}
                              isTutorProfile={true}
                            />
                          )}
                        </div>
                        <div className="my-[16px] h-[1px] w-full bg-gray-200" />
                        {/* 시간 */}
                        <div className="w-full">
                          <div className="max-h-[319px] w-full sm:max-h-none">
                            {selectedDay && (
                              <div className="w-full">
                                <div className="flex w-full items-center justify-between">
                                  {currentTimeslots.filter((timeslot) => timeslot.is_today).length > 0 && (
                                    <div className="flex items-center">
                                      <div className="h-[12px] w-[20px] rounded-[2px] border-[1px] border-gray-300 bg-red-50"></div>
                                      <div className="ml-[8px]">{t(n_book_lessons.last_min_time)}</div>
                                    </div>
                                  )}
                                </div>
                                {currentTimeslots.filter((timeslot) => timeslot.hour < 12).length > 0 && (
                                  <div className="mt-[16px] w-full">
                                    <div className="Body2-14Bd text-gray-500">{t(n_book_lessons.AM_hours)}</div>
                                    <div className="mt-[8px] grid grid-cols-4 gap-[8px]">
                                      {currentTimeslots
                                        .filter((timeslot) => timeslot.hour < 12)
                                        .map((timeslot: ITimeslot, index: number) => {
                                          return (
                                            <TimeChipItem
                                              key={index}
                                              timeslot={timeslot}
                                              handleSelectTime={handleSelectTime}
                                            />
                                          )
                                        })}
                                    </div>
                                  </div>
                                )}
                                {currentTimeslots.filter((timeslot) => timeslot.hour >= 12).length > 0 && (
                                  <div className="mt-[16px] w-full">
                                    <div className="Body2-14Bd text-gray-500">{t(n_book_lessons.PM_hours)}</div>
                                    <div className="mt-[8px] grid grid-cols-4 gap-[8px]">
                                      {currentTimeslots
                                        .filter((timeslot) => timeslot.hour >= 12)
                                        .map((timeslot: ITimeslot, index: number) => {
                                          return (
                                            <TimeChipItem
                                              key={index}
                                              timeslot={timeslot}
                                              handleSelectTime={handleSelectTime}
                                            />
                                          )
                                        })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="mt-[8px] grid grid-cols-1 border-t-[1px] border-gray-200 bg-white p-[16px] sm:grid-cols-2 sm:rounded-[8px] sm:border-[1px]">
                        {/* 날짜 */}
                        <div>
                          <div className="Body1-16Bd text-gray-500">{t(n_book_lessons.time_picker.date)}</div>
                          <div className="mt-[14px]">
                            {selectedDay && (
                              <ScheduleCalendar
                                selectedDay={selectedDay}
                                setSelectedDay={setSelectedDay}
                                classtimes={availableTimes}
                                isTutorProfile={true}
                              />
                            )}
                          </div>
                        </div>
                        {/* 시간 */}
                        <div className="w-full sm:pl-[20px]">
                          <div className="Body1-16Bd text-gray-500">{t(n_book_lessons.time_picker.time)}</div>
                          <div className="mt-[10px] max-h-[319px] w-full sm:max-h-none">
                            {selectedDay && (
                              <div className="w-full">
                                <div className="flex w-full items-center justify-between">
                                  <div className="Title2-18Bd">{dateTitle(selectedDay, isLocaleKO)}</div>
                                  {currentTimeslots.filter((timeslot) => timeslot.is_today).length > 0 && (
                                    <div className="flex items-center">
                                      <div className="h-[12px] w-[20px] rounded-[2px] border-[1px] border-gray-300 bg-red-100"></div>
                                      <div className="ml-[8px]">{t(n_book_lessons.last_min_time)}</div>
                                    </div>
                                  )}
                                </div>
                                {currentTimeslots.filter((timeslot) => timeslot.hour < 12).length > 0 && (
                                  <div className="mt-[16px] w-full">
                                    <div className="Body2-14Bd text-gray-500">{t(n_book_lessons.AM_hours)}</div>
                                    <div className="mt-[8px] grid grid-cols-4 gap-[8px]">
                                      {currentTimeslots
                                        .filter((timeslot) => timeslot.hour < 12)
                                        .map((timeslot: ITimeslot, index: number) => {
                                          return (
                                            <TimeChipItem
                                              key={index}
                                              timeslot={timeslot}
                                              handleSelectTime={handleSelectTime}
                                            />
                                          )
                                        })}
                                    </div>
                                  </div>
                                )}
                                {currentTimeslots.filter((timeslot) => timeslot.hour >= 12).length > 0 && (
                                  <div className="mt-[16px] w-full">
                                    <div className="Body2-14Bd text-gray-500">{t(n_book_lessons.PM_hours)}</div>
                                    <div className="mt-[8px] grid grid-cols-4 gap-[8px]">
                                      {currentTimeslots
                                        .filter((timeslot) => timeslot.hour >= 12)
                                        .map((timeslot: ITimeslot, index: number) => {
                                          return (
                                            <TimeChipItem
                                              key={index}
                                              timeslot={timeslot}
                                              handleSelectTime={handleSelectTime}
                                            />
                                          )
                                        })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="h-[80px] sm:h-[900px]"></div>
          </div>
        ))}
      <NoPackageModal
        showNoAvailablePackage={showNoAvailablePackage}
        setShowNoAvailablePackage={setShowNoAvailablePackage}
      />
      {couponBundlesData && (
        <PackageSelectModal
          showPackageSelectModal={showPackageSelectModal}
          setShowPackageSelectModal={setShowPackageSelectModal}
          applyInfo={applyInfo}
          setApplyInfo={setApplyInfo}
          data={couponBundlesData}
          onClose={() => {
            resetTimeslot()
            setShowPackageSelectModal(false)
          }}
        />
      )}
      <TodayScheduleAlertModal
        schedules={schedules}
        showTodayScheduleModal={showTodayScheduleModal}
        setShowTodayScheduleModal={setShowTodayScheduleModal}
        handleSubmit={handleSubmit}
        showSidePopup={showSidePopup}
      />
      {showErrorMessage.length > 0 && (
        <DSModal
          id="TextOnlyMOneButtonPopup"
          isOpen={showErrorMessagePopup}
          setIsOpen={setShowErrorMessagePopup}
          size={isMobile ? 'sm' : 'lg'}
        >
          <DSModal.Content>{showErrorMessage}</DSModal.Content>
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton size={BUTTON_SIZE.MD} onClick={() => setShowErrorMessagePopup(false)}>
              <DSButton.Label>{t(n_common.btn_confirm)}</DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        </DSModal>
      )}
      {showNoAvailableCreditsMessage.length > 0 && (
        <DSModal
          id="TextOnlyMOneButtonPopup"
          isOpen={showNoAvailableCreditsPopup}
          setIsOpen={setShowNoAvailableCreditsPopup}
          size={isMobile ? 'sm' : 'lg'}
        >
          <DSModal.Title>
            <div>{showNoAvailableCreditsMessage}</div>
          </DSModal.Title>
          <DSModal.Content>
            <div className="Body1-16Md text-gray-700">{t(n_book_lessons.no_credit.subtitle)}</div>
          </DSModal.Content>
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.OUTLINE}
              customBgColor={COLORS.WHITE}
              className="whitespace-nowrap"
              onClick={() => setShowNoAvailableCreditsPopup(false)}
            >
              <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
            </DSButton>
            <DSButton
              className="whitespace-nowrap"
              size={BUTTON_SIZE.MD}
              onClick={() =>
                router.push(`${urls.student.newPortal.pricing.index}`, {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: {
                    url: getFullUrl(`${urls.student.portal.pricing.index}?app=true`),
                  },
                })
              }
            >
              <DSButton.Label>{t(n_book_lessons.no_credit.btn_purchase_credit)}</DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        </DSModal>
      )}
      <TimeSlotUnavailableModal
        isOpen={isTimeSlotUnavailableModalOpen}
        onOpen={() => setIsTimeSlotUnavailableModalOpen((prev) => !prev)}
      />
    </div>
  )
}
export default TutorScheduleLesson
